import type { Widget } from '../contract.js';
import { span } from './engine.js';

/** Text sizing, ported from the prototype (04 §8). An estimate, not a measurement: keep the formula. */

export interface TextStyle {
  readonly fs: number;
  readonly lh: number;
  readonly cols: number;
  readonly fw: 400 | 500;
  readonly ls: '-0.015em' | '0';
}

export const FS_LADDER = [12, 14, 16, 18, 21, 24, 28, 32, 38, 44] as const;
export const TAP_TO_WRITE = 'Tap to write';

const lineHeight = (fs: number): number => (fs >= 24 ? 1.16 : fs >= 17 ? 1.3 : 1.5);

/** `fsStyle`: the style of an explicit size. */
export function fsStyle(fs: number, cols = 1): TextStyle {
  return { fs, lh: lineHeight(fs), cols: cols || 1, fw: fs >= 20 ? 500 : 400, ls: fs >= 20 ? '-0.015em' : '0' };
}

/** `textFit`: the largest size from 34 px down to 12 px that fits a `W × H` widget; 11 px if none does. */
export function textFit(text: string | undefined, W: number, H: number): TextStyle {
  const s = text || '', n = Math.max(1, s.length), paras = Math.max(1, s.split(/\n\s*\n/).length), cols = W >= 400 && n > 320 ? 2 : 1;
  const cw = (W - 28 - (cols - 1) * 24) / cols, ch = H - 24;
  for (let fs = 34; fs > 11; fs -= 1) {
    const lh = lineHeight(fs), cpl = Math.max(4, Math.floor(cw / (fs * 0.53)));
    const lines = Math.ceil(n * 1.08 / cpl) + (paras - 1) * 0.75;
    if (lines * fs * lh <= ch * cols && fs * lh <= ch) return { fs, lh, cols, fw: fs >= 20 ? 500 : 400, ls: fs >= 20 ? '-0.015em' : '0' };
  }
  return { fs: 11, lh: 1.5, cols, fw: 400, ls: '0' };
}

/** `textFsOf`: the size a text currently shows at (explicit, or automatic; empty text measures as "Tap to write"). */
export function textFsOf(item: Pick<Widget, 'fs' | 'text' | 'w' | 'h'>): number {
  return item.fs || textFit(item.text || TAP_TO_WRITE, span(item.w), span(item.h)).fs;
}

/** `textSize`: the next ladder value above (`dir > 0`) or below the current size, or null at the end. */
export function nextTextSize(item: Pick<Widget, 'fs' | 'text' | 'w' | 'h'>, dir: number): number | null {
  const current = textFsOf(item);
  if (dir > 0) return FS_LADDER.find((value) => value > current) ?? null;
  return [...FS_LADDER].reverse().find((value) => value < current) ?? null;
}

/** Paragraphs of a text widget: split on blank lines, blank paragraphs dropped. */
export const paragraphs = (text: string | undefined): string[] => (text || '').split(/\n\s*\n/).filter((part) => part.trim().length > 0);
